import React, { useState, useEffect, useRef, useCallback } from "react";
import { BANNERS_DATA, BANNERS_PERIODO } from "./bannersData";
import { C, CHART_COLORS } from "./theme";
// pptxgenjs se carga dinámicamente en exportDashboardPpt

// ── Jerarquía de entidades ────────────────────────────────────────────────────
// El grupo es también una entidad en bannersData (ej. "59 G-7")
// Seleccionar el grupo solo selecciona esa entidad agregada, no sus miembros
const JERARQUIA = [
  {
    entidad: "5 Total Banca Múltiple",
    bancos: [],
  },
  {
    entidad: "59 G-7",
    bancos: [
      "40002 Banamex","40012 BBVA México","40014 Santander","40021 HSBC",
      "40036 Inbursa","40044 Scotiabank","40072 Banorte",
    ],
  },
  {
    entidad: "60 Bancos Cambiarios",
    bancos: ["40112 Monex","40136 Intercam Banco","40143 CIBanco","40145 Banco Base"],
  },
  {
    entidad: "61 Inversión y Servicios Financieros",
    bancos: [
      "40059 Invex","40062 Afirme","40106 Bank of America","40108 MUFG Bank",
      "40110 J.P. Morgan","40124 Citi México","40126 Credit Suisse","40129 Barclays",
      "40139 BIAfirme","40148 Pagatodo","40160 Banco S3","40164 BNP Paribas México",
    ],
  },
  {
    entidad: "62 Comercial Mediano",
    bancos: [
      "40030 Banco del Bajío","40042 Banca Mifel","40058 Banregio",
      "40113 Ve por Más","40132 Multiva","40156 Sabadell",
    ],
  },
  {
    entidad: "63 Comercial Pequeño",
    bancos: [
      "40060 Bansí","40128 Kapital (Antes Autofin)","40133 Actinver","40138 Ualá",
      "40147 Bankaool","40150 Inmobiliario Mexicano","40151 Dondé Banco",
      "40152 Bancrea","40154 Covalto","40155 ICBC","40157 Shinhan",
      "40158 Mizuho Bank","40159 Bank of China","40162 KEB Hana México",
    ],
  },
  {
    entidad: "64 Créditos a los Hogares",
    bancos: [
      "40127 Banco Azteca","40130 Compartamos","40137 Bancoppel","40140 Consubanco",
      "40141 Volkswagen Bank","40149 Banfeliz (Antes Forjadores)","40165 Banco Bineo",
      "40167 Hey Banco","40169 Openbank","40170 Revolut Bank",
    ],
  },
];

// Filtra solo bancos que existen en bannersData
const entidadesEnDatos = new Set(BANNERS_DATA.banner2.map(r => r.nombre_entidad));

// ── Catálogo de métricas — clasificación dinámica por keywords ───────────────
// Renombres de despliegue (SOLO visual; NO cambia la llave con la que se cruza BANNERS_DATA)
const DISPLAY_LABELS = {
  "Captación": "Captación Total",
};
// Normaliza labels quitando el periodo embebido (ej. "Feb 2026", "Mar 2026")
const normLabel = (label) => {
  const base = label.replace(/\b(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic|jan|apr|aug|sep|oct|nov|dec)\w*\.?\s+\d{4}/gi, "").trim();
  return DISPLAY_LABELS[base] || base;
};

// Reglas de clasificación por keywords (orden importa — primera coincidencia gana)
const GRUPOS_REGLAS = [
  {
    nombre: "Reservas EPRC",
    banner: "banner1",
    keywords: [], // todo banner1 va aquí
  },
  {
    nombre: "Riesgo Crediticio",
    banner: "banner3",
    keywords: ["imor","imora","icor","tda","quita","castigo","cartera etapa","eprc / cartera"],
  },
  {
    nombre: "Solvencia y Liquidez",
    banner: "banner3",
    keywords: ["icap","capital neto","capital fundamental","cet","ccb","ccf","ccom","liquidez","sobrante","faltante","apr","requerimiento"],
  },
  {
    nombre: "Rentabilidad",
    banner: "banner3",
    keywords: ["roa","roe","tasa implícita","t.i.","tasa implici"],
  },
  {
    nombre: "Balance",
    banner: "banner2",
    keywords: ["activo","pasivo","capital contable","capital ganado","cartera","captación","caja","instr","d.e.i.","d. corto","d.largo","préstamos interb","créditos comerciales","créditos de consumo","créditos hipotecarios","bancos"],
  },
  {
    nombre: "Resultados",
    banner: "banner2",
    keywords: ["resultado","margen","ingresos","gastos","comisión","comisiones","intermediación","intereses","admón","eprc"],
  },
];

// Clasificar cada label en su grupo según keywords
function clasificarLabel(label, banner) {
  const norm = normLabel(label).toLowerCase();
  for (const grupo of GRUPOS_REGLAS) {
    if (grupo.banner !== banner) continue;
    if (grupo.keywords.length === 0) return grupo.nombre;
    if (grupo.keywords.some(k => norm.includes(k))) return grupo.nombre;
  }
  return GRUPOS_REGLAS.find(g => g.banner === banner)?.nombre || banner;
}

// Construir METRICAS dinámicamente desde bannersData
const METRICAS = (() => {
  const grupos = {};
  for (const banner of ["banner1", "banner2", "banner3"]) {
    const labels = [...new Set(BANNERS_DATA[banner].map(r => r.label))].sort();
    for (const label of labels) {
      const grupo = clasificarLabel(label, banner);
      if (!grupos[grupo]) grupos[grupo] = { banner, items: [] };
      grupos[grupo].items.push(label);
    }
  }
  return grupos;
})();

const TIPOS_GRAFICA = [
  { id: "line",      label: "Línea",         icon: "📈" },
  { id: "bar",       label: "Barras",        icon: "📊" },
  { id: "bar-group", label: "Barras Agrup.", icon: "▦"  },
  { id: "area",      label: "Área Apilada",  icon: "◼"  },
  { id: "treemap",   label: "Treemap",       icon: "⊞"  },
  { id: "scatter",   label: "Dispersión",    icon: "⁘"  },
];

const MAX_METRICAS = 3;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtVal(v, tipo) {
  if (v == null) return "—";
  const t = (tipo || "monto").toLowerCase();
  if (t.includes("porcentaje") || t === "pp" || t === "%") return v.toFixed(2) + "%";
  if (t.includes("veces") || t === "x") return v.toFixed(2) + "x";
  // Montos SIEMPRE en MDP (millones de pesos), para cuadrar 1:1 con la tabla/portal CNBV y la narrativa
  return (v / 1e6).toLocaleString("es-MX", { maximumFractionDigits: 1 }) + " mdp";
}

function getBannerForMetrica(label) {
  for (const [, grupo] of Object.entries(METRICAS)) {
    if (grupo.items.includes(label)) return grupo.banner;
  }
  return null;
}

function getTipoForMetrica(label) {
  const banner = getBannerForMetrica(label);
  if (!banner) return "monto";
  const rec = BANNERS_DATA[banner].find(r => r.label === label);
  return rec ? rec.tipo : "monto";
}

function getPeriodos(label) {
  const banner = getBannerForMetrica(label);
  if (!banner) return [];
  // Eje de periodos de ESTA metrica (no del primer registro cualquiera del banner):
  // asi conceptos de balance que llegan a junio no quedan recortados por metricas
  // de capital que se reportan con un mes de rezago. Toma el historico mas reciente.
  const recs = BANNERS_DATA[banner].filter(r => r.label === label && r.historico?.length > 0);
  if (!recs.length) return [];
  const rec = recs.reduce((a, b) => {
    const la = a.historico[a.historico.length - 1]?.periodo || 0;
    const lb = b.historico[b.historico.length - 1]?.periodo || 0;
    if (lb !== la) return lb > la ? b : a;
    return b.historico.length > a.historico.length ? b : a;
  });
  return rec.historico.map(h => h.periodo);
}

// ── Treemap (canvas nativo) ───────────────────────────────────────────────────
function Treemap({ data }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !data.length) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.offsetWidth || 800;
    const H = canvas.offsetHeight || 380;
    canvas.width = W; canvas.height = H;
    ctx.clearRect(0, 0, W, H);

    const total = data.reduce((s, d) => s + Math.abs(d.valor), 0);
    if (!total) return;

    let rects = [];
    function squarify(items, x, y, w, h) {
      if (!items.length) return;
      if (items.length === 1) { rects.push({ ...items[0], x, y, w, h }); return; }
      const half = Math.floor(items.length / 2);
      const sumA = items.slice(0, half).reduce((s, i) => s + Math.abs(i.valor), 0);
      const ratio = sumA / total;
      if (w >= h) {
        squarify(items.slice(0, half), x, y, w * ratio, h);
        squarify(items.slice(half), x + w * ratio, y, w * (1 - ratio), h);
      } else {
        squarify(items.slice(0, half), x, y, w, h * ratio);
        squarify(items.slice(half), x, y + h * ratio, w, h * (1 - ratio));
      }
    }
    squarify([...data].sort((a, b) => Math.abs(b.valor) - Math.abs(a.valor)), 0, 0, W, H);

    rects.forEach((r, i) => {
      const col = CHART_COLORS[i % CHART_COLORS.length];
      ctx.fillStyle = col + "cc";
      ctx.fillRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
      ctx.strokeStyle = C.bg;
      ctx.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2);
      if (r.w > 60 && r.h > 30) {
        const name = r.label.replace(/^\d+ /, "");
        ctx.fillStyle = C.text;
        ctx.font = `bold ${Math.min(13, r.w / 8)}px monospace`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(name.length > 14 ? name.slice(0, 13) + "…" : name, r.x + r.w / 2, r.y + r.h / 2 - 8);
        ctx.font = `${Math.min(11, r.w / 9)}px monospace`;
        ctx.fillStyle = C.sub;
        ctx.fillText(fmtVal(r.valor, r.tipo), r.x + r.w / 2, r.y + r.h / 2 + 10);
      }
    });
  }, [data]);

  return <canvas ref={canvasRef} style={{ width: "100%", height: "100%", borderRadius: 8, border: `1px solid ${C.border}` }} />;
}

// ── Widget de gráfica ─────────────────────────────────────────────────────────
function ChartWidget({ config, onRemove, backendUrl, onCanvasReady, onInsightReady }) {
  const canvasRef  = useRef(null);
  const chartRef   = useRef(null);
  const [insight, setInsight]   = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  // Notificar canvas ref al padre para export PPT
  useEffect(() => { if (onCanvasReady) onCanvasReady(canvasRef); }, []);
  // Notificar insight al padre cuando llega
  useEffect(() => { if (insight && onInsightReady) onInsightReady(insight); }, [insight]);
  const { metricas, entidades, tipo, horizonte } = config;

  useEffect(() => {
    if (tipo === "treemap") return;
    if (!window.Chart) return;

    // Destruir instancia anterior de este canvas
    if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; }
    const canvas = canvasRef.current;
    if (!canvas) return;

    const allPeriodos = getPeriodos(metricas[0]);
    const periodos = allPeriodos.slice(-horizonte);
    const labels = periodos.map(p => { const s = String(p); return s.slice(0,4) + "-" + s.slice(4); });

    if (tipo === "scatter") {
      const mx = metricas[0], my = metricas[1] || metricas[0];
      const bx = getBannerForMetrica(mx), by = getBannerForMetrica(my);
      if (!bx || !by) return;
      // Un dataset por entidad para que Chart.js muestre leyenda individual
      const datasets = entidades.map((ent, i) => {
        const rx = BANNERS_DATA[bx].find(r => r.label === mx && r.nombre_entidad === ent);
        const ry = BANNERS_DATA[by].find(r => r.label === my && r.nombre_entidad === ent);
        const color = CHART_COLORS[i % CHART_COLORS.length];
        return {
          label: ent.replace(/^\d+ /,""),
          data: rx && ry ? [{ x: rx.valor_actual, y: ry.valor_actual }] : [],
          backgroundColor: color + "cc",
          borderColor: color,
          pointRadius: 9,
          pointHoverRadius: 11,
        };
      }).filter(d => d.data.length > 0);

      chartRef.current = new window.Chart(canvas, {
        type: "scatter",
        data: { datasets },
        options: {
          responsive: true, maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: "bottom",
              labels: { color: C.sub, font: { size: 13 }, boxWidth: 10, padding: 14 },
            },
            tooltip: { callbacks: {
              label: ctx => `${ctx.dataset.label}: (${fmtVal(ctx.parsed.x, getTipoForMetrica(mx))}, ${fmtVal(ctx.parsed.y, getTipoForMetrica(my))})`,
            }},
          },
          scales: {
            x: { ticks: { color: C.sub, callback: (v) => fmtVal(v, getTipoForMetrica(mx)) }, grid: { color: C.border }, title: { display: true, text: normLabel(mx), color: C.sub } },
            y: { ticks: { color: C.sub, callback: (v) => fmtVal(v, getTipoForMetrica(my)) }, grid: { color: C.border }, title: { display: true, text: normLabel(my), color: C.sub } },
          },
        },
      });
      return;
    }

    let colorIdx = 0;
    const datasets = [];
    for (const metrica of metricas) {
      const banner = getBannerForMetrica(metrica);
      if (!banner) continue;
      for (const ent of entidades) {
        const row = BANNERS_DATA[banner].find(r => r.label === metrica && r.nombre_entidad === ent);
        if (!row?.historico) continue;
        const valores = periodos.map(p => { const h = row.historico.find(h => h.periodo === p); return h ? h.valor : null; });
        const color = CHART_COLORS[colorIdx++ % CHART_COLORS.length];
        const label = metricas.length > 1 ? `${normLabel(metrica)} · ${ent.replace(/^\d+ /,"")}` : ent.replace(/^\d+ /,"");
        const isStacked = tipo === "area" || tipo === "bar";
        if (tipo === "area") {
          datasets.push({ label, data: valores, borderColor: color, backgroundColor: color + "55", fill: true, tension: 0.3, pointRadius: 3 });
        } else if (tipo === "bar" || tipo === "bar-group") {
          datasets.push({ label, data: valores, backgroundColor: color + "cc", borderColor: color, borderWidth: 1 });
        } else {
          datasets.push({ label, data: valores, borderColor: color, backgroundColor: color + "22", tension: 0.3, pointRadius: 3, fill: false });
        }
      }
    }

    const isStacked = tipo === "area" || tipo === "bar";
    chartRef.current = new window.Chart(canvas, {
      type: tipo === "line" || tipo === "area" ? "line" : "bar",
      data: { labels, datasets },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: { display: true, position: "bottom", labels: { color: C.sub, font: { size: 13 }, boxWidth: 12, padding: 14 } },
          tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${fmtVal(ctx.parsed.y, getTipoForMetrica(metricas[0]))}` } },
        },
        scales: {
          x: { stacked: isStacked, ticks: { color: C.sub }, grid: { color: C.border + "88" } },
          y: { stacked: isStacked, ticks: { color: C.sub, callback: (v) => fmtVal(v, getTipoForMetrica(metricas[0])) }, grid: { color: C.border + "88" } },
        },
      },
    });

    return () => { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [config]);

  // ── Análisis IA via /narrativa-grafica (ruta ligera: 1 párrafo, Haiku) ─────
  const handleAnalizar = async () => {
    setLoadingAI(true); setInsight(null);
    try {
      // Ruta ligera: solo las métricas efectivamente graficadas × entidades × horizonte.
      const filtrar = (banner) =>
        BANNERS_DATA[banner]
          .filter(r => entidades.includes(r.nombre_entidad) && metricas.includes(r.label))
          .map(r => ({ ...r, historico: r.historico ? r.historico.slice(-horizonte) : [] }));

      const nombresEntidades = entidades.map(e => e.replace(/^\d+ /, ""));

      const resp = await fetch(`${backendUrl}/narrativa-grafica`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entidades:       nombresEntidades,
          metricas,
          periodo:         BANNERS_PERIODO,
          horizonte_meses: horizonte,
          tipo_grafica:    tipo,
          datos_b1: filtrar("banner1"),
          datos_b2: filtrar("banner2"),
          datos_b3: filtrar("banner3"),
        }),
      });
      const data = await resp.json();
      setInsight(data.narrativa || "Sin respuesta del modelo.");
    } catch (e) {
      setInsight("Error al conectar con el modelo: " + e.message);
    }
    setLoadingAI(false);
  };

  const handleExportHtml = () => exportWidgetHtml(canvasRef, config, insight);

  // Treemap
  if (tipo === "treemap") {
    const banner  = getBannerForMetrica(metricas[0]);
    const tipo_m  = getTipoForMetrica(metricas[0]);
    const treemapData = banner
      ? entidades.map(ent => {
          const row = BANNERS_DATA[banner].find(r => r.label === metricas[0] && r.nombre_entidad === ent);
          return row ? { label: ent, valor: row.valor_actual, tipo: tipo_m } : null;
        }).filter(Boolean)
      : [];
    return (
      <div style={cardStyle}>
        <WidgetHeader config={config} onRemove={onRemove} onAnalizar={handleAnalizar} loadingAI={loadingAI} onExportHtml={handleExportHtml} />
        <div style={{ flex: 1, minHeight: 260 }}><Treemap data={treemapData} /></div>
        {insight && <InsightBox text={insight} />}
      </div>
    );
  }

  return (
    <div style={cardStyle}>
      <WidgetHeader config={config} onRemove={onRemove} onAnalizar={handleAnalizar} loadingAI={loadingAI} onExportHtml={handleExportHtml} />
      <div style={{ flex: 1, minHeight: 260, position: "relative" }}>
        <canvas ref={canvasRef} />
      </div>
      {insight && <InsightBox text={insight} />}
    </div>
  );
}

const cardStyle = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 16,
  display: "flex", flexDirection: "column", gap: 10, minHeight: 450,
};

function WidgetHeader({ config, onRemove, onAnalizar, loadingAI, onExportHtml }) {
  const tipoLabel = TIPOS_GRAFICA.find(t => t.id === config.tipo)?.label || config.tipo;
  const _MESES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
  const _pStr = String(BANNERS_PERIODO);
  const periodoLabel = `${_MESES[parseInt(_pStr.slice(4), 10) - 1]} ${_pStr.slice(0, 4)}`;
  const periodoTxt = (config.tipo === "scatter" || config.tipo === "treemap") ? periodoLabel : `últimos ${config.horizonte}m · al ${periodoLabel}`;
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: "monospace", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {config.metricas.map(normLabel).join(" · ")}
        </div>
        <div style={{ fontSize: 13, color: C.sub, marginTop: 2 }}>
          {config.entidades.map(e => e.replace(/^\d+ /,"")).join(", ")} · {tipoLabel} · {periodoTxt}
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
        <button onClick={onAnalizar} disabled={loadingAI} style={{
          background: C.gold + "22", border: `1px solid ${C.gold}44`, borderRadius: 6,
          color: C.gold, fontSize: 13, padding: "3px 8px", cursor: "pointer", fontFamily: "monospace",
          opacity: loadingAI ? 0.5 : 1,
        }}>
          {loadingAI ? "Analizando…" : "✦ IA"}
        </button>
        <button onClick={onExportHtml} style={{
          background: "none", border: `1px solid ${C.border}`, borderRadius: 6,
          color: C.muted, fontSize: 13, padding: "3px 8px", cursor: "pointer", fontFamily: "monospace",
        }}>↓ HTML</button>
        <button onClick={onRemove} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 18 }}>✕</button>
      </div>
    </div>
  );
}

function InsightBox({ text }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.gold}33`, borderRadius: 8,
      padding: "10px 12px", fontSize: 14, color: C.text, fontFamily: "monospace", lineHeight: 1.6 }}>
      <span style={{ color: C.gold, marginRight: 6 }}>✦ EMRISK AI</span>{text}
    </div>
  );
}

// ── Panel de configuración ────────────────────────────────────────────────────
function ConfigPanel({ onAgregar }) {
  const [grupoMetrica, setGrupoMetrica]   = useState("Riesgo Crediticio");
  const [metricasSel, setMetricasSel]     = useState([]);
  const [entidadesSel, setEntidadesSel]   = useState([]);
  const [tipo, setTipo]                   = useState("line");
  const [horizonte, setHorizonte]         = useState(12);
  const [busqMetrica, setBusqMetrica]     = useState("");
  const [busqEntidad, setBusqEntidad]     = useState("");
  const [gruposExp, setGruposExp]         = useState({ "59 G-7": true }); // G-7 expandido por defecto

  const metricasGrupo = METRICAS[grupoMetrica]?.items || [];
  const maxMetricas = tipo === "treemap" ? 1 : MAX_METRICAS;  // el treemap solo grafica 1 métrica

  const toggleMetrica = (m) => {
    setMetricasSel(prev => {
      if (prev.includes(m)) return prev.filter(x => x !== m);
      if (prev.length >= maxMetricas) return prev;
      return [...prev, m];
    });
  };

  const toggleEntidad = (nombre) => setEntidadesSel(prev =>
    prev.includes(nombre) ? prev.filter(x => x !== nombre) : [...prev, nombre]
  );

  const toggleGrupoExp = (entidad) => setGruposExp(prev => ({ ...prev, [entidad]: !prev[entidad] }));

  const canAdd = metricasSel.length > 0 && entidadesSel.length > 0;

  const sectionLabel = { fontSize: 14, color: C.sub, fontFamily: "monospace",
    letterSpacing: 1, textTransform: "uppercase", marginBottom: 6 };
  const inputStyle = {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
    color: C.text, padding: "6px 10px", fontSize: 15, width: "100%", outline: "none",
    fontFamily: "monospace", boxSizing: "border-box",
  };

  return (
    <div style={{ width: 268, flexShrink: 0, background: C.surface, borderRight: `1px solid ${C.border}`,
      display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Header */}
      <div style={{ padding: "14px 16px 10px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: C.gold, fontFamily: "monospace" }}>◱ CONSTRUCTOR</div>
        <div style={{ fontSize: 13, color: C.sub, marginTop: 2 }}>
          Periodo base: {String(BANNERS_PERIODO).slice(0,4)}-{String(BANNERS_PERIODO).slice(4)}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Tipo */}
        <div>
          <div style={sectionLabel}>01 · Tipo de gráfica</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4 }}>
            {TIPOS_GRAFICA.map(t => (
              <button key={t.id} onClick={() => { setTipo(t.id); if (t.id === "treemap") setMetricasSel(prev => prev.slice(0, 1)); }} style={{
                background: tipo === t.id ? C.gold + "22" : C.card,
                border: `1px solid ${tipo === t.id ? C.gold : C.border}`,
                borderRadius: 6, color: tipo === t.id ? C.gold : C.sub,
                padding: "6px 6px", fontSize: 14, cursor: "pointer",
                fontFamily: "monospace", textAlign: "left",
              }}>{t.icon} {t.label}</button>
            ))}
          </div>
        </div>

        {/* Horizonte */}
        <div>
          <div style={sectionLabel}>02 · Horizonte</div>
          <div style={{ display: "flex", gap: 4 }}>
            {[3,6,12].map(h => (
              <button key={h} onClick={() => setHorizonte(h)} style={{
                flex: 1, background: horizonte === h ? C.gold + "22" : C.card,
                border: `1px solid ${horizonte === h ? C.gold : C.border}`,
                borderRadius: 6, color: horizonte === h ? C.gold : C.sub,
                padding: "7px 0", fontSize: 15, cursor: "pointer", fontFamily: "monospace",
              }}>{h}m</button>
            ))}
          </div>
        </div>

        {/* Métricas */}
        <div>
          <div style={sectionLabel}>
            03 · Métricas
            <span style={{ color: metricasSel.length >= maxMetricas ? C.amber : C.gold, marginLeft: 4 }}>
              ({metricasSel.length}/{maxMetricas})
            </span>
          </div>
          <select value={grupoMetrica} onChange={e => { setGrupoMetrica(e.target.value); }}
            style={{ ...inputStyle, marginBottom: 6 }}>
            {Object.keys(METRICAS).map(g => <option key={g} value={g}>{g}</option>)}
          </select>
          <input placeholder="Buscar métrica..." value={busqMetrica}
            onChange={e => setBusqMetrica(e.target.value)} style={{ ...inputStyle, marginBottom: 6 }} />
          <div style={{ maxHeight: 170, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2 }}>
            {metricasGrupo.filter(m => m.toLowerCase().includes(busqMetrica.toLowerCase())).map(m => {
              const sel = metricasSel.includes(m);
              const disabled = !sel && metricasSel.length >= maxMetricas;
              return (
                <label key={m} style={{ display: "flex", alignItems: "center", gap: 6, cursor: disabled ? "not-allowed" : "pointer",
                  padding: "3px 6px", borderRadius: 4, background: sel ? C.gold + "18" : "none",
                  opacity: disabled ? 0.4 : 1 }}>
                  <input type="checkbox" checked={sel} onChange={() => toggleMetrica(m)}
                    disabled={disabled} style={{ accentColor: C.gold }} />
                  <span style={{ fontSize: 15, color: sel ? C.gold : C.sub, fontFamily: "monospace" }}>{m}</span>
                </label>
              );
            })}
          </div>
          {metricasSel.length >= maxMetricas && (
            <div style={{ fontSize: 12, color: C.amber, fontFamily: "monospace", marginTop: 4 }}>
              {tipo === "treemap" ? "El treemap grafica una sola métrica" : `Máximo ${maxMetricas} métricas por gráfica`}
            </div>
          )}
        </div>

        {/* Entidades — jerarquía */}
        <div>
          <div style={sectionLabel}>
            04 · Entidades
            {entidadesSel.length > 0 && <span style={{ color: C.gold, marginLeft: 4 }}>({entidadesSel.length})</span>}
          </div>
          <input placeholder="Buscar banco..." value={busqEntidad}
            onChange={e => setBusqEntidad(e.target.value)} style={{ ...inputStyle, marginBottom: 6 }} />

          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {JERARQUIA.map(grupo => {
              const { entidad, bancos } = grupo;
              if (!entidadesEnDatos.has(entidad) &&
                  !bancos.some(b => entidadesEnDatos.has(b))) return null;

              const bancosVisibles = bancos.filter(b =>
                entidadesEnDatos.has(b) &&
                (b.toLowerCase().includes(busqEntidad.toLowerCase()) ||
                 entidad.toLowerCase().includes(busqEntidad.toLowerCase()))
              );
              // Si hay búsqueda y ni el grupo ni sus bancos coinciden, ocultar
              if (busqEntidad &&
                  !entidad.toLowerCase().includes(busqEntidad.toLowerCase()) &&
                  !bancosVisibles.length) return null;

              const expanded  = gruposExp[entidad];
              const grupoSel  = entidadesSel.includes(entidad);
              const label     = entidad.replace(/^\d+ /, "");

              return (
                <div key={entidad} style={{ marginBottom: 2 }}>
                  {/* Fila del grupo — es una entidad seleccionable */}
                  <div style={{ display: "flex", alignItems: "center", gap: 4, padding: "3px 4px",
                    borderRadius: 4, background: grupoSel ? C.gold + "15" : "none" }}>
                    {/* Botón expandir — solo si tiene bancos hijos */}
                    <span onClick={() => bancos.length && toggleGrupoExp(entidad)}
                      style={{ fontSize: 12, color: C.muted, width: 12, cursor: bancos.length ? "pointer" : "default",
                        userSelect: "none" }}>
                      {bancos.length ? (expanded ? "▾" : "▸") : "·"}
                    </span>
                    {/* Checkbox del grupo como entidad */}
                    <input type="checkbox" checked={grupoSel}
                      onChange={() => toggleEntidad(entidad)}
                      style={{ accentColor: C.gold, width: 12, height: 12 }} />
                    <span onClick={() => toggleEntidad(entidad)}
                      style={{ fontSize: 15, fontWeight: 600,
                        color: grupoSel ? C.gold : C.text,
                        fontFamily: "monospace", flex: 1, cursor: "pointer" }}>
                      {label}
                    </span>
                    {bancos.length > 0 &&
                      <span style={{ fontSize: 12, color: C.muted }}>{bancosVisibles.length}</span>}
                  </div>

                  {/* Bancos miembros — independientes del grupo */}
                  {(expanded || busqEntidad) && bancosVisibles.length > 0 && (
                    <div style={{ paddingLeft: 22, display: "flex", flexDirection: "column", gap: 1 }}>
                      {bancosVisibles.map(b => {
                        const sel = entidadesSel.includes(b);
                        return (
                          <label key={b} style={{ display: "flex", alignItems: "center", gap: 6,
                            cursor: "pointer", padding: "2px 6px", borderRadius: 4,
                            background: sel ? C.gold + "15" : "none" }}>
                            <input type="checkbox" checked={sel} onChange={() => toggleEntidad(b)}
                              style={{ accentColor: C.gold, width: 11, height: 11 }} />
                            <span style={{ fontSize: 15, color: sel ? C.gold : C.sub, fontFamily: "monospace" }}>
                              {b.replace(/^\d+ /, "")}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {entidadesSel.length > 0 && (
            <button onClick={() => setEntidadesSel([])} style={{
              marginTop: 6, fontSize: 12, padding: "2px 8px", borderRadius: 4,
              background: "none", border: `1px solid ${C.border}`, color: C.muted,
              cursor: "pointer", fontFamily: "monospace",
            }}>Limpiar selección</button>
          )}
        </div>

      </div>

      {/* Botón agregar */}
      <div style={{ padding: "10px 14px", borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
        <button onClick={() => {
          if (!canAdd) return;
          onAgregar({ metricas: [...metricasSel], entidades: [...entidadesSel], tipo, horizonte });
          setMetricasSel([]);
        }} disabled={!canAdd} style={{
          width: "100%", padding: "9px 0",
          background: canAdd ? C.gold : C.muted + "33",
          border: "none", borderRadius: 8,
          color: canAdd ? C.bg : C.muted,
          fontFamily: "monospace", fontWeight: 700, fontSize: 15,
          cursor: canAdd ? "pointer" : "not-allowed",
        }}>+ AGREGAR GRÁFICA</button>
      </div>
    </div>
  );
}

// ── Export HTML de widget Chart.js ────────────────────────────────────────────
function exportWidgetHtml(canvasRef, config, insight) {
  const canvas = canvasRef?.current;
  const imgSrc = canvas ? canvas.toDataURL("image/png") : null;
  const tipoLabel = TIPOS_GRAFICA.find(t => t.id === config.tipo)?.label || config.tipo;
  const titulo = config.metricas.map(normLabel).join(" · ");
  const _MESES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
  const _pStr = String(BANNERS_PERIODO);
  const periodoLabel = `${_MESES[parseInt(_pStr.slice(4), 10) - 1]} ${_pStr.slice(0, 4)}`;
  const periodoTxt = (config.tipo === "scatter" || config.tipo === "treemap") ? periodoLabel : `últimos ${config.horizonte}m · al ${periodoLabel}`;
  const subtitulo = config.entidades.map(e => e.replace(/^\d+ /,"")).join(", ") + ` · ${tipoLabel} · ${periodoTxt}`;
  const narrativaHtml = insight
    ? insight.replace(/\n/g, "<br/>").replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    : "";

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8">
  <title>EMRISK — ${titulo}</title>
  <style>
    body { background: #070c12; color: #dde4ee; font-family: 'Segoe UI', sans-serif; padding: 30px; margin: 0; }
    .container { max-width: 1100px; margin: 0 auto; display: flex; flex-direction: column; gap: 20px; }
    header { border-bottom: 1px solid #1a2535; padding-bottom: 15px; display: flex; align-items: center; gap: 12px; }
    .logo { width: 32px; height: 32px; background: linear-gradient(135deg, #c8a96e, #8a6d3b); border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #070c12; font-weight: bold; font-size: 16px; }
    .brand { color: #c8a96e; font-size: 15px; font-family: monospace; }
    .tagline { color: #8aa4b8; font-size: 10px; font-family: monospace; }
    .card { background: #0d1520; border: 1px solid #1a2535; border-radius: 12px; padding: 20px; }
    .label { color: #c8a96e; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; font-family: monospace; margin-bottom: 8px; }
    .subtitle { color: #8aa4b8; font-size: 11px; font-family: monospace; margin-bottom: 16px; }
    img { width: 100%; border-radius: 8px; }
    .analysis-text { line-height: 1.7; color: #9ab4cc; font-size: 14px; }
    .analysis-text strong { color: #c8a96e; }
    .footer { color: #556a7a; font-size: 10px; font-family: monospace; margin-top: 24px; border-top: 1px solid #1a2535; padding-top: 12px; }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <div class="logo">E</div>
      <div>
        <div class="brand">EMRISK</div>
        <div class="tagline">Inteligencia financiera con datos CNBV</div>
      </div>
    </header>
    <div class="card">
      <div class="label">${titulo}</div>
      <div class="subtitle">${subtitulo}</div>
      ${imgSrc ? `<img src="${imgSrc}" alt="${titulo}" />` : "<p style='color:#8aa4b8'>Gráfica no disponible</p>"}
    </div>
    ${narrativaHtml ? `
    <div class="card" style="border-left: 3px solid #c8a96e;">
      <div class="label">✦ Análisis EMRISK AI</div>
      <div class="analysis-text">${narrativaHtml}</div>
    </div>` : ""}
    <div class="footer">Fuente: CNBV · EMRISK · Datos al periodo ${String(BANNERS_PERIODO).slice(0,4)}-${String(BANNERS_PERIODO).slice(4)}</div>
  </div>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `EMRISK_${titulo.replace(/[^a-zA-Z0-9]/g,"_")}.html`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Export PPT ────────────────────────────────────────────────────────────────
async function exportDashboardPpt({ widgets, widgetRefs, insightMap, dashCharts, userEmail }) {
  const PptxGenJS = (await import("pptxgenjs")).default;
  const pres = new PptxGenJS();
  pres.layout  = "LAYOUT_16x9";
  pres.title   = "EMRISK Dashboard";
  pres.author  = userEmail || "EMRISK";

  const BG    = "070c12";
  const GOLD  = "c8a96e";
  const CARD  = "111b28";
  const BORD  = "1a2535";
  const TEXT  = "dde4ee";
  const SUB   = "8aa4b8";
  const DIM   = "556a7a";

  const periodo = `${String(BANNERS_PERIODO).slice(0,4)}-${String(BANNERS_PERIODO).slice(4)}`;
  const fechaHoy = new Date().toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });

  // ── Diapositiva 1: Portada ───────────────────────────────────────────────
  const cover = pres.addSlide();
  cover.background = { color: BG };

  // Línea dorada izquierda
  cover.addShape(pres.shapes.RECTANGLE, { x: 0, y: 0, w: 0.08, h: 5.625, fill: { color: GOLD } });

  // Logo cuadrado
  cover.addShape(pres.shapes.RECTANGLE, {
    x: 0.5, y: 1.6, w: 0.7, h: 0.7,
    fill: { color: GOLD }, line: { color: GOLD },
  });
  cover.addText("E", { x: 0.5, y: 1.6, w: 0.7, h: 0.7, fontSize: 28, bold: true, color: BG, align: "center", valign: "middle", margin: 0 });

  // Título EMRISK
  cover.addText("EMRISK", { x: 1.35, y: 1.65, w: 7, h: 0.55, fontSize: 36, bold: true, color: GOLD, fontFace: "Consolas", charSpacing: 6, margin: 0 });
  cover.addText("Inteligencia financiera con datos CNBV", { x: 1.35, y: 2.2, w: 7, h: 0.35, fontSize: 16, color: SUB, fontFace: "Consolas", charSpacing: 1, margin: 0 });

  // Separador
  cover.addShape(pres.shapes.RECTANGLE, { x: 0.5, y: 2.75, w: 9, h: 0.02, fill: { color: BORD } });

  // Info usuario y fecha
  cover.addText(userEmail || "", { x: 0.5, y: 3.05, w: 9, h: 0.35, fontSize: 15, color: TEXT, fontFace: "Consolas", margin: 0 });
  cover.addText(`${fechaHoy}  ·  Periodo de datos: ${periodo}  ·  Fuente: CNBV`, {
    x: 0.5, y: 3.45, w: 9, h: 0.3, fontSize: 14, color: DIM, fontFace: "Consolas", margin: 0,
  });

  // Nº de gráficas
  const total = widgets.length + dashCharts.length;
  cover.addText(`${total} visualización${total !== 1 ? "es" : ""} exportadas`, {
    x: 0.5, y: 4.1, w: 4, h: 0.35, fontSize: 15, color: GOLD, fontFace: "Consolas", margin: 0,
  });

  // ── Diapositivas por widget (constructor) ────────────────────────────────
  for (const w of widgets) {
    const canvas = widgetRefs[w.id]?.current;
    if (!canvas) continue;
    const imgData = canvas.toDataURL("image/png").replace("data:image/png;base64,", "image/png;base64,");
    const insight = insightMap[w.id] || null;
    const titulo  = w.config.metricas.map(normLabel).join(" · ");
    const subtit  = w.config.entidades.map(e => e.replace(/^\d+ /,"")).join(", ") +
                    ` · ${w.config.tipo} · últimos ${w.config.horizonte}m`;
    _addChartSlide(pres, { imgData, titulo, subtit, insight, BG, GOLD, CARD, BORD, TEXT, SUB, DIM });
  }

  // ── Diapositivas por gráfica del chat (Plotly) ───────────────────────────
  for (const c of dashCharts) {
    let imgData = null;
    try {
      if (window.Plotly && c.fig) {
        const png = await window.Plotly.toImage(
          document.querySelector(".plotly-chart-container") || document.body,
          { format: "png", width: 900, height: 450 }
        );
        imgData = png.replace("data:image/png;base64,", "image/png;base64,");
      }
    } catch (_) {}
    _addChartSlide(pres, {
      imgData, titulo: c.title || "Gráfica",
      subtit: "Desde el Chat · EMRISK AI",
      insight: c.narrativa || null,
      BG, GOLD, CARD, BORD, TEXT, SUB, DIM,
    });
  }

  await pres.writeFile({ fileName: `EMRISK_Dashboard_${periodo}.pptx` });
}

function _addChartSlide(pres, { imgData, titulo, subtit, insight, BG, GOLD, CARD, BORD, TEXT, SUB, DIM }) {
  const slide = pres.addSlide();
  slide.background = { color: BG };

  // Franja superior dorada
  slide.addShape(pres.shapes.RECTANGLE, { x: 0, y: 0, w: 10, h: 0.06, fill: { color: GOLD } });

  // Título
  slide.addText(titulo, { x: 0.4, y: 0.18, w: 9.2, h: 0.45, fontSize: 16, bold: true, color: GOLD, fontFace: "Consolas", margin: 0 });
  slide.addText(subtit, { x: 0.4, y: 0.62, w: 9.2, h: 0.28, fontSize: 13, color: SUB, fontFace: "Consolas", margin: 0 });

  // Separador
  slide.addShape(pres.shapes.RECTANGLE, { x: 0.4, y: 0.95, w: 9.2, h: 0.01, fill: { color: BORD } });

  const hasInsight = !!insight;

  // Estimación anticipada para saber si habrá continuación
  const CHARS_RIGHT = 680;
  const necesitaContinuacion = hasInsight && insight.length > CHARS_RIGHT;

  // Layout: gráfica izquierda, análisis derecha, continuación abajo si aplica
  const CHART_X  = 0.4;
  const CHART_W  = hasInsight ? 5.8 : 9.2;
  const CHART_H  = necesitaContinuacion ? 2.6 : 3.8;  // más pequeña si hay continuación
  const CHART_Y  = 1.05;
  const TEXT_X   = 6.4;
  const TEXT_W   = 3.2;
  const FOOTER_Y = 5.38;

  // Imagen de la gráfica
  if (imgData) {
    slide.addImage({ data: imgData, x: CHART_X, y: CHART_Y, w: CHART_W, h: CHART_H,
      sizing: { type: "contain", w: CHART_W, h: CHART_H } });
  } else {
    slide.addShape(pres.shapes.RECTANGLE, { x: CHART_X, y: CHART_Y, w: CHART_W, h: CHART_H,
      fill: { color: CARD }, line: { color: BORD } });
    slide.addText("Vista previa no disponible", { x: CHART_X, y: CHART_Y, w: CHART_W, h: CHART_H,
      fontSize: 14, color: SUB, align: "center", valign: "middle", fontFace: "Consolas" });
  }

  // Análisis IA — columna derecha + desborde abajo (sin repetir texto)
  if (hasInsight) {
    const rightH = CHART_H;
    const belowY = CHART_Y + CHART_H + 0.1;
    const belowH = FOOTER_Y - belowY - 0.05;

    const parte1 = insight.slice(0, CHARS_RIGHT);
    const parte2 = insight.slice(CHARS_RIGHT).trim();

    // Columna derecha
    slide.addShape(pres.shapes.RECTANGLE, { x: TEXT_X, y: CHART_Y, w: TEXT_W, h: rightH,
      fill: { color: CARD }, line: { color: BORD } });
    slide.addShape(pres.shapes.RECTANGLE, { x: TEXT_X, y: CHART_Y, w: 0.05, h: rightH,
      fill: { color: GOLD } });
    slide.addText("✦  EMRISK AI", { x: TEXT_X + 0.12, y: CHART_Y + 0.1, w: TEXT_W - 0.15, h: 0.25,
      fontSize: 12, bold: true, color: GOLD, fontFace: "Consolas", margin: 0 });
    slide.addText(parte1, {
      x: TEXT_X + 0.12, y: CHART_Y + 0.35, w: TEXT_W - 0.15, h: rightH - 0.45,
      fontSize: 12.5, color: SUB, fontFace: "Consolas", margin: 0, wrap: true, valign: "top",
    });

    // Fila inferior — solo si hay texto que no cupo
    if (parte2 && belowH > 0.3) {
      slide.addShape(pres.shapes.RECTANGLE, { x: CHART_X, y: belowY, w: 9.2, h: belowH,
        fill: { color: CARD }, line: { color: BORD } });
      slide.addShape(pres.shapes.RECTANGLE, { x: CHART_X, y: belowY, w: 0.05, h: belowH,
        fill: { color: GOLD } });
      slide.addText("↳ continuación", { x: CHART_X + 0.12, y: belowY + 0.06, w: 3, h: 0.2,
        fontSize: 11, color: GOLD, fontFace: "Consolas", italic: true, margin: 0 });
      slide.addText(parte2, {
        x: CHART_X + 0.12, y: belowY + 0.28, w: 9.0, h: belowH - 0.35,
        fontSize: 12.5, color: SUB, fontFace: "Consolas", margin: 0, wrap: true, valign: "top",
      });
    }
  } else {
    // Sin análisis — fila inferior vacía
    const belowY = CHART_Y + CHART_H + 0.1;
    const belowH = FOOTER_Y - belowY - 0.05;
    if (belowH > 0) {
      // espacio vacío — no necesita nada
    }
  }

  // Footer
  slide.addText(`EMRISK · Fuente: CNBV · ${new Date().getFullYear()}`, {
    x: 0.4, y: FOOTER_Y, w: 9.2, h: 0.2, fontSize: 11, color: DIM, fontFace: "Consolas", margin: 0,
  });
}

// ── Componente principal ──────────────────────────────────────────────────────
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://cnbv-agente-7bdezditva-uc.a.run.app";

export default function GraficasView({ dashCharts = [], setDashCharts = () => {}, ChartCard = null, userEmail = "" }) {
  const [widgets, setWidgets] = useState([]);
  const [chartJsLoaded, setChartJsLoaded] = useState(!!window.Chart);
  const [exportingPpt, setExportingPpt] = useState(false);
  const widgetRefs   = useRef({});  // { [id]: canvasRef }
  const insightMap   = useRef({});  // { [id]: insightText }

  useEffect(() => {
    if (window.Chart) { setChartJsLoaded(true); return; }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.0/chart.umd.min.js";
    script.onload = () => setChartJsLoaded(true);
    document.head.appendChild(script);
  }, []);

  const agregarWidget = useCallback((config) => {
    setWidgets(prev => [...prev, { id: Date.now() + Math.random(), config }]);
  }, []);

  const removerWidget = useCallback((id) => {
    setWidgets(prev => prev.filter(w => w.id !== id));
  }, []);

  return (
    <div style={{ display: "flex", flex: 1, overflow: "hidden", background: C.bg }}>
      <ConfigPanel onAgregar={agregarWidget} />

      <div style={{ flex: 1, overflow: "auto", padding: 16 }}>

        {/* ── Botón exportar PPT ── */}
        {(widgets.length > 0 || dashCharts.length > 0) && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <button
              onClick={async () => {
                setExportingPpt(true);
                try {
                  await exportDashboardPpt({ widgets, widgetRefs: widgetRefs.current, insightMap: insightMap.current, dashCharts, userEmail });
                } catch(e) { console.error("PPT error:", e); }
                setExportingPpt(false);
              }}
              disabled={exportingPpt}
              style={{
                background: exportingPpt ? C.muted + "33" : C.gold + "22",
                border: `1px solid ${exportingPpt ? C.border : C.gold + "66"}`,
                borderRadius: 7, padding: "7px 16px",
                color: exportingPpt ? C.muted : C.gold,
                fontFamily: "monospace", fontSize: 15,
                cursor: exportingPpt ? "not-allowed" : "pointer",
              }}>
              {exportingPpt ? "Generando PPT…" : "↓ Exportar PPT"}
            </button>
          </div>
        )}

        {!chartJsLoaded && (
          <div style={{ color: C.sub, fontFamily: "monospace", fontSize: 15, padding: 20 }}>
            Cargando motor de gráficas…
          </div>
        )}

        {chartJsLoaded && widgets.length === 0 && (
          <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 12, color: C.muted }}>
            <div style={{ fontSize: 40 }}>◱</div>
            <div style={{ fontFamily: "monospace", fontSize: 16 }}>
              Selecciona métricas y entidades para construir tu primera gráfica
            </div>
            <div style={{ fontSize: 14, color: C.muted, maxWidth: 420, textAlign: "center" }}>
              Puedes agregar múltiples gráficas y combinarlas en el canvas.
              Datos: {String(BANNERS_PERIODO).slice(0,4)}-{String(BANNERS_PERIODO).slice(4)} · CNBV
            </div>
          </div>
        )}

        {/* ── Gráficas desde el Chat ── */}
        {dashCharts.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, color: C.sub, fontFamily: "monospace", letterSpacing: 1, textTransform: "uppercase" }}>
                ◈ Desde el Chat <span style={{ color: C.gold }}>({dashCharts.length})</span>
              </div>
              <button onClick={() => setDashCharts([])} style={{
                background: "none", border: `1px solid ${C.border}`, borderRadius: 5,
                padding: "3px 10px", fontSize: 13, color: C.muted, cursor: "pointer", fontFamily: "monospace",
              }}>Limpiar</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {dashCharts.map((c, i) =>
                ChartCard ? (
                  <ChartCard
                    key={i}
                    fig={c.fig}
                    title={c.title}
                    narrative={c.narrativa || null}
                    compact={true}
                    inDash
                    onRemove={() => setDashCharts(prev => prev.filter((_,j) => j !== i))}
                  />
                ) : null
              )}
            </div>
            <div style={{ borderBottom: `1px solid ${C.border}`, margin: "20px 0" }} />
          </div>
        )}

        {/* ── Gráficas del Constructor ── */}
        {chartJsLoaded && widgets.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 12, alignItems: "start" }}>
            {widgets.map(w => (
              <ChartWidget key={w.id} id={w.id} config={w.config}
                onRemove={() => removerWidget(w.id)} backendUrl={BACKEND_URL}
                onCanvasReady={(ref) => { widgetRefs.current[w.id] = ref; }}
                onInsightReady={(text) => { insightMap.current[w.id] = text; }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
